import { createElement, useMemo } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import type { ApexOptions } from "apexcharts";
import { t, useI18n } from "@bookingcom/lingojs-react";
import type { I18nChildContext } from "@bookingcom/lingojs-react";
import type { AverageDiscountAirlineSeries, AverageDiscountChartProps } from "./AverageDiscountChart";
import { AverageDiscountTooltip } from "./AverageDiscountTooltip";
import { CHART_DATE_TICK_AMOUNT, formatChartDate, formatChartDateAxisLabel } from "../../../utils/chart";
import { AIRLINE_SERIES_COLORS } from "../ChartConstants";
import type { Metric } from "../../../types";

const totalSampleSize = (metric: Metric): number =>
  metric.timeSeries.dataPoints.reduce((sum, dp) => sum + (dp.sampleSize ?? 0), 0);

const weightedAverage = (entries: { value: number; weight: number }[]): number => {
  const totalWeight = entries.reduce((sum, { weight }) => sum + weight, 0);
  if (totalWeight === 0) {
    return entries.reduce((sum, { value }) => sum + value, 0) / entries.length;
  }
  return entries.reduce((sum, { value, weight }) => sum + value * weight, 0) / totalWeight;
};

const buildSingleAirlineOptions = (categories: number[], i18n: I18nChildContext): ApexOptions => ({
  chart: {
    type: "line",
    toolbar: { show: false },
    zoom: { enabled: false },
  },
  stroke: {
    curve: "straight",
    width: [0, 3],
  },
  colors: ["var(--bui_color_action_foreground)", "var(--bui_color_callout_background)"],
  fill: {
    opacity: [1, 1],
  },
  markers: {
    size: [0, 4],
    colors: ["var(--bui_color_action_foreground)", "var(--bui_color_callout_background)"],
    strokeColors: ["var(--bui_color_action_foreground)", "var(--bui_color_callout_background)"],
    strokeWidth: 0,
    hover: { size: 6 },
  },
  plotOptions: {
    bar: {
      columnWidth: "60%",
      borderRadius: 0,
    },
  },
  xaxis: {
    type: "category",
    categories,
    tickPlacement: "on",
    tickAmount: CHART_DATE_TICK_AMOUNT,
    title: {
      text: i18n.trans(t("flights_airline_portal_realtime_performance_metric_two_xaxis")),
      offsetY: 24,
    },
    labels: {
      rotate: 0,
      rotateAlways: false,
      formatter: formatChartDateAxisLabel,
    },
    axisBorder: { show: false },
    axisTicks: { show: false },
    crosshairs: { show: false },
    tooltip: { enabled: false },
  },
  yaxis: [
    {
      tickAmount: 6,
      labels: {
        formatter: (val: number) => `€${Math.round(val)}`,
        padding: 40,
      },
    },
    {
      opposite: true,
      labels: {
        formatter: (val: number) => `${Math.round(val)}%`,
        padding: 40,
      },
    },
  ],
  grid: {
    borderColor: "var(--bui_color_border_alt)",
    strokeDashArray: 0,
    xaxis: { lines: { show: false } },
    yaxis: { lines: { show: true } },
    padding: { top: 24, left: 44, right: 44, bottom: 30 },
  },
  tooltip: {
    x: { format: "d MMM yyyy" },
    y: {
      formatter: (val: number, opts) => {
        if (opts.seriesIndex === 0) {
          return `€${Math.round(val)}`;
        }
        return `${val.toFixed(1)}%`;
      },
    },
  },
  legend: {
    show: true,
    position: "top",
    horizontalAlign: "right",
    markers: { size: 7, shape: "circle" },
    offsetY: 0,
    onItemClick: { toggleDataSeries: false },
  },
});

const buildMultiAirlineOptions = (
  series: AverageDiscountAirlineSeries[],
  categories: number[],
  i18n: I18nChildContext
): ApexOptions => {
  const euroValueByAirlineAndTime = new Map<string, Map<number, number>>(
    series.map(({ airlineCode, euroMetric }) => [
      airlineCode,
      new Map(euroMetric.timeSeries.dataPoints.map((dp) => [new Date(dp.time).getTime(), dp.value])),
    ])
  );

  return {
    chart: {
      type: "line",
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    dataLabels: { enabled: false },
    colors: AIRLINE_SERIES_COLORS,
    stroke: {
      curve: "straight",
      width: 2,
    },
    markers: {
      size: 5,
      strokeWidth: 2,
      hover: { size: 7 },
    },
    xaxis: {
      type: "category",
      categories,
      tickPlacement: "on",
      tickAmount: CHART_DATE_TICK_AMOUNT,
      title: {
        text: i18n.trans(t("flights_airline_portal_realtime_performance_metric_two_xaxis")),
        offsetY: 24,
      },
      labels: {
        rotate: 0,
        rotateAlways: false,
        formatter: formatChartDateAxisLabel,
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
      crosshairs: { show: false },
      tooltip: { enabled: false },
    },
    yaxis: {
      labels: {
        formatter: (val: number) => `${Math.round(val)}%`,
        padding: 40,
      },
    },
    grid: {
      borderColor: "var(--bui_color_border_alt)",
      strokeDashArray: 0,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: { top: 0, left: 44, right: 44, bottom: 30 },
    },
    tooltip: {
      shared: false,
      intersect: false,
      x: { format: "d MMM yyyy" },
      custom: ({ series: seriesValues, dataPointIndex, w }) => {
        const time = categories[dataPointIndex];
        const rows = w.globals.seriesNames.map((name: string, index: number) => {
          const airlineCode = series[index]?.airlineCode ?? "";
          const percentage: number | undefined = seriesValues[index]?.[dataPointIndex] ?? undefined;
          const euro: number | undefined = euroValueByAirlineAndTime.get(airlineCode)?.get(time);
          return {
            name,
            percentage,
            euro,
            color: w.globals.colors[index],
          };
        });
        return renderToStaticMarkup(
          createElement(AverageDiscountTooltip, {
            title: formatChartDate(time),
            rows,
          })
        );
      },
    },
    legend: {
      show: true,
      showForSingleSeries: true,
      position: "top",
      horizontalAlign: "right",
      markers: { size: 7, shape: "circle" },
      offsetY: 0,
      onItemClick: { toggleDataSeries: false },
    },
  };
};

export const useAverageDiscountChartController = ({ series, multiAirline = false }: AverageDiscountChartProps) => {
  const i18n = useI18n();
  const [firstAirlineSeries] = series;
  const averageDiscountEuro = Math.round(
    weightedAverage(
      series.map(({ euroMetric }) => ({ value: euroMetric.average.value, weight: totalSampleSize(euroMetric) }))
    )
  );
  const averageDiscountPercentage = weightedAverage(
    series.map(({ percentageMetric }) => ({
      value: percentageMetric.average.value,
      weight: totalSampleSize(percentageMetric),
    }))
  ).toFixed(1);

  const categories = useMemo(() => {
    if (!multiAirline) {
      return firstAirlineSeries.euroMetric.timeSeries.dataPoints.map((dp) => new Date(dp.time).getTime());
    }
    const times = new Set<number>();
    series.forEach(({ percentageMetric }) =>
      percentageMetric.timeSeries.dataPoints.forEach((dp) => times.add(new Date(dp.time).getTime()))
    );
    return [...times].sort((a, b) => a - b);
  }, [series, multiAirline, firstAirlineSeries.euroMetric.timeSeries.dataPoints]);

  const chartSeries: ApexAxisChartSeries = useMemo(() => {
    if (!multiAirline) {
      return [
        {
          name: i18n.trans(t("flights_airline_portal_realtime_performance_metric_two_yaxis_a")),
          type: "bar",
          data: firstAirlineSeries.euroMetric.timeSeries.dataPoints.map((dp) => dp.value),
        },
        {
          name: i18n.trans(t("flights_airline_portal_realtime_performance_metric_two_yaxis_b")),
          type: "line",
          data: firstAirlineSeries.percentageMetric.timeSeries.dataPoints.map((dp) => dp.value),
        },
      ];
    }
    return series.map(({ airlineCode, airlineName, percentageMetric }) => {
      const valueByTime = new Map(
        percentageMetric.timeSeries.dataPoints.map((dp) => [new Date(dp.time).getTime(), dp.value])
      );
      return {
        name: `${airlineName} (${airlineCode})`,
        data: categories.map((time) => valueByTime.get(time) ?? null),
      };
    });
  }, [series, categories, multiAirline, i18n, firstAirlineSeries]);

  const options: ApexOptions = useMemo(
    () =>
      multiAirline ? buildMultiAirlineOptions(series, categories, i18n) : buildSingleAirlineOptions(categories, i18n),
    [series, categories, multiAirline, i18n]
  );

  return {
    averageDiscountEuro,
    averageDiscountPercentage,
    options,
    series: chartSeries,
  };
};
